import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { Stack, CurrencyDollar, TrendUp, Hash, Info } from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, qtd } from '../format';
import { IAuditoriaEmpresa, IAcumulador } from '../types';
import { KpiCard } from '../components/KpiCard';
import { EmptyState } from '../components/EmptyState';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { UI } from '../ui';

interface AcumuladoresScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Cruzamentos: navegar a outro submódulo (Fiscal...). */
  onScreen: (screen: string) => void;
}

export function AcumuladoresScreen({ empresa, periodo, onScreen }: AcumuladoresScreenProps) {
  const { getAcumuladores } = useAuditoriaInteligente();

  const { data, isFetching } = useQuery<IAcumulador[]>({
    queryKey: ['ai-acumuladores', { id: empresa?.id, ...periodo }],
    queryFn: getAcumuladores,
    enabled: !!empresa,
  });

  const lista = useMemo(() => data ?? [], [data]);

  const totais = useMemo(() => {
    const volume = lista.reduce(
      (s, a) => s + num(a.valor_saidas) + num(a.valor_entradas) + num(a.valor_servicos),
      0,
    );
    const faturamento = lista.filter(a => a.ifat === 'S').reduce((s, a) => s + num(a.saldo), 0);
    const topSaida = [...lista].sort(
      (a, b) =>
        num(b.valor_saidas) + num(b.valor_servicos) - (num(a.valor_saidas) + num(a.valor_servicos)),
    )[0];
    const negativos = lista.filter(a => num(a.saldo) < 0).length;
    // A lista pode ter mais de uma linha pro MESMO código (acumulador renomeado
    // ou IFAT alternado no meio do range) — o KPI conta códigos distintos.
    const codigos = new Set(lista.map(a => a.codigo)).size;
    return { volume, faturamento, topSaida, negativos, codigos };
  }, [lista]);

  const topBar = useMemo(
    () =>
      [...lista]
        .filter(
          a => num(a.valor_saidas) > 0 || num(a.valor_entradas) > 0 || num(a.valor_servicos) > 0,
        )
        .sort(
          (a, b) =>
            num(b.valor_saidas) +
            num(b.valor_entradas) +
            num(b.valor_servicos) -
            (num(a.valor_saidas) + num(a.valor_entradas) + num(a.valor_servicos)),
        )
        .slice(0, 10)
        .map(a => ({
          codigo: a.codigo,
          saidas: num(a.valor_saidas),
          entradas: num(a.valor_entradas),
          servicos: num(a.valor_servicos),
        })),
    [lista],
  );

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver os acumuladores fiscais."
        icon={Stack}
      />
    );
  }

  return (
    <Box>
      <Flex
        align="center"
        gap={2.5}
        p={3}
        mb={5}
        borderRadius="12px"
        bg={UI.infoSoft}
        border={`1px solid ${UI.brand}22`}
      >
        <Box color={UI.brand} flexShrink={0}>
          <Info size={16} weight="fill" />
        </Box>
        <Text fontSize="12.5px" color={UI.ink}>
          Movimento <b>mensal por acumulador</b> dentro do período selecionado no topo — só meses já
          cobertos pela extração v2. O selo <b>FAT</b> marca acumuladores que compõem o Relatório de
          Faturamento do Domínio (IFAT_ACU = S).
          {totais.negativos > 0 && (
            <Text as="span" color={UI.neg} fontWeight={700}>
              {' '}
              {totais.negativos} com saldo negativo — possível sinal de auditoria.
            </Text>
          )}
        </Text>
      </Flex>

      <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
        <KpiCard
          label="Acumuladores"
          value={totais.codigos}
          icon={Stack}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Quantidade de acumuladores fiscais com movimento no período."
              dados="extract_acumuladores.codigo (linhas por external_id no período)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Volume total"
          value={formatCurrency(totais.volume)}
          icon={CurrencyDollar}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Soma do valor de saídas, entradas e serviços de todos os acumuladores no período."
              dados="extract_acumuladores.valor_saidas + valor_entradas + valor_servicos"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Maior saída"
          value={totais.topSaida?.codigo ?? '—'}
          sub={totais.topSaida?.nome}
          icon={TrendUp}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          isLoading={isFetching}
          onClick={() => onScreen('fiscal')}
          info={
            <InfoBloco
              oque="Acumulador com o maior valor de saídas + serviços no período."
              dados="extract_acumuladores.codigo/nome (máximo de valor_saidas + valor_servicos)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Faturamento (Domínio)"
          value={formatCurrency(totais.faturamento)}
          icon={Hash}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          isLoading={isFetching}
          info={
            <InfoBloco
              oque="Faturamento na regra do Relatório de Faturamento do Domínio: saldo (saídas − devoluções + serviços) dos acumuladores com IFAT = S. Considera SÓ os meses já cobertos pela extração v2 (sem fallback por notas) — pode ficar abaixo do KPI das outras telas até a re-extração cobrir o período."
              dados="extract_acumuladores.saldo (SUM onde ifat = 'S')"
              filtro={FILTRO.empresa}
            />
          }
        />
      </Grid>

      {topBar.length > 0 && (
        <Box
          bg={UI.glass}
          border="1px solid"
          borderColor={UI.glassBorder}
          borderRadius="16px"
          backdropFilter={UI.glassBlur}
          boxShadow={UI.glassShadow}
          p={5}
          mb={5}
        >
          <Text fontSize="13px" fontWeight={700} mb={4} color={UI.ink}>
            Top acumuladores — entradas × saídas × serviços
          </Text>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={topBar}>
              <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
              <XAxis dataKey="codigo" fontSize={11} stroke={UI.muted} />
              <YAxis
                fontSize={11}
                stroke={UI.muted}
                width={70}
                tickFormatter={v => formatCurrency(v)}
              />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Legend />
              <Bar
                dataKey="saidas"
                name="Saídas"
                stackId="ac"
                fill={UI.brand}
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="servicos"
                name="Serviços"
                stackId="ac"
                fill={UI.pos}
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="entradas"
                name="Entradas"
                stackId="ac"
                fill={UI.warn}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      <Box
        borderRadius="16px"
        overflow="hidden"
        bg="rgba(255,255,255,0.7)"
        backdropFilter="blur(14px) saturate(160%)"
        border="1px solid rgba(255,255,255,0.85)"
        boxShadow="0 12px 34px -18px rgba(15,23,42,0.35)"
      >
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={UI.rowHover}>
              <Tr>
                <Th>Código</Th>
                <Th>Acumulador</Th>
                <Th>Fat.</Th>
                <Th isNumeric>Qtd Saídas</Th>
                <Th isNumeric>Valor Saídas</Th>
                <Th isNumeric>Qtd Entradas</Th>
                <Th isNumeric>Valor Entradas</Th>
                <Th isNumeric>Qtd Serviços</Th>
                <Th isNumeric>Valor Serviços</Th>
                <Th isNumeric>Saldo</Th>
                <Th isNumeric>Ticket S / E</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isFetching && (
                <Tr>
                  <Td colSpan={11} textAlign="center" py={8}>
                    <Spinner />
                  </Td>
                </Tr>
              )}
              {!isFetching &&
                lista.map(a => {
                  const negativo = num(a.saldo) < 0;
                  const ticketEnt = num(a.qtd_entradas)
                    ? num(a.valor_entradas) / num(a.qtd_entradas)
                    : 0;
                  return (
                    <Tr
                      key={`${a.codigo}-${a.nome}-${a.ifat}`}
                      _hover={{ bg: UI.rowHover }}
                      bg={negativo ? `${UI.neg}0A` : undefined}
                    >
                      <Td fontWeight={700}>{a.codigo}</Td>
                      <Td maxW="280px" whiteSpace="normal">
                        {a.nome}
                      </Td>
                      <Td>
                        {a.ifat === 'S' ? (
                          <Badge colorScheme="purple" fontSize="10px">
                            FAT
                          </Badge>
                        ) : (
                          <Text as="span" color={UI.muted}>
                            —
                          </Text>
                        )}
                      </Td>
                      <Td isNumeric>{qtd(a.qtd_saidas)}</Td>
                      <Td isNumeric>{formatCurrency(a.valor_saidas)}</Td>
                      <Td isNumeric>{qtd(a.qtd_entradas)}</Td>
                      <Td isNumeric>{formatCurrency(a.valor_entradas)}</Td>
                      <Td isNumeric>{qtd(a.qtd_servicos)}</Td>
                      <Td isNumeric>{formatCurrency(a.valor_servicos)}</Td>
                      <Td isNumeric fontWeight={700} color={negativo ? UI.neg : UI.ink}>
                        {formatCurrency(a.saldo)}
                      </Td>
                      <Td isNumeric color={UI.muted} fontSize="11.5px">
                        {num(a.ticket_medio_saida) ? formatCurrency(a.ticket_medio_saida) : '—'}
                        {' / '}
                        {ticketEnt ? formatCurrency(ticketEnt) : '—'}
                      </Td>
                    </Tr>
                  );
                })}
              {!isFetching && lista.length === 0 && (
                <Tr>
                  <Td colSpan={11} textAlign="center" py={8} color={UI.muted}>
                    Nenhum acumulador com movimento no período.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
